import { 
  collection, 
  doc, 
  getDocs, 
  setDoc,
  updateDoc, 
  query,
  orderBy,
  limit,
  serverTimestamp,
  deleteField
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { 
  GeneralAssignmentConsolidated, 
  GeneralAssignmentInfo, 
  GeneralStudentStatus,
  GeneralAssignmentConsolidatedV2,
  GeneralAssignmentInfoV2,
  GeneralStudentStatusV2
} from '../types/index';
import { 
  Assignment,
  AssignmentStatusValue
} from '../types/alarm';
import { assignmentSubmissionImageService } from './assignmentSubmissionImageService';

// ===== 배열 ↔ 중첩 객체 변환 유틸리티 =====

/**
 * 배열 형태의 studentStatuses를 중첩 객체로 변환
 */
function studentStatusesArrayToObject(
  arr: GeneralStudentStatus[]
): { [studentId: string]: GeneralStudentStatusV2 } {
  const obj: { [studentId: string]: GeneralStudentStatusV2 } = {};
  for (const status of arr) {
    const { studentId, ...rest } = status;
    obj[studentId] = rest;
  }
  return obj;
}

/**
 * 중첩 객체 형태의 studentStatuses를 배열로 변환 (하위 호환성)
 */
function studentStatusesObjectToArray(
  obj: { [studentId: string]: GeneralStudentStatusV2 }
): GeneralStudentStatus[] {
  return Object.entries(obj).map(([studentId, status]) => ({
    studentId,
    ...status
  }));
}

/**
 * 배열 형태의 assignments를 중첩 객체로 변환
 */
function assignmentsArrayToObject(
  arr: GeneralAssignmentInfo[]
): { [assignmentId: string]: GeneralAssignmentInfoV2 } {
  const obj: { [assignmentId: string]: GeneralAssignmentInfoV2 } = {};
  for (const assignment of arr) {
    const { assignmentId, studentStatuses, ...rest } = assignment;
    obj[assignmentId] = {
      ...rest,
      studentStatuses: studentStatusesArrayToObject(studentStatuses)
    };
  }
  return obj;
}

/**
 * 중첩 객체 형태의 assignments를 배열로 변환 (하위 호환성)
 */
function assignmentsObjectToArray(
  obj: { [assignmentId: string]: GeneralAssignmentInfoV2 }
): GeneralAssignmentInfo[] {
  return Object.entries(obj).map(([assignmentId, assignment]) => ({
    assignmentId,
    ...assignment,
    studentStatuses: studentStatusesObjectToArray(assignment.studentStatuses)
  }));
}

/**
 * 데이터가 배열인지 객체인지 확인
 */
function isArrayData(data: any): boolean {
  return Array.isArray(data);
}

const GENERAL_CACHE_PREFIX = 'ga_';

export class GeneralAssignmentConsolidatedService {
  private userId: string;
  private classId: string;
  private memberGroup: string;
  private maxDocumentSize: number = 950 * 1024; // 950KB (1MB 제한의 95%)
  private useNestedObjects: boolean = true; // V2 중첩 객체 사용 여부

  private static __consolidatedCache = new Map<string, { v1: GeneralAssignmentConsolidated[]; v2: GeneralAssignmentConsolidatedV2[] }>();

  private getCacheKey(): string {
    return `${GENERAL_CACHE_PREFIX}${this.userId}_${this.classId}_${this.memberGroup}`;
  }

  private invalidateConsolidatedCache(): void {
    GeneralAssignmentConsolidatedService.__consolidatedCache.delete(this.getCacheKey());
  }

  constructor(userId: string, classId: string, memberGroup?: string) {
    this.userId = userId;
    this.classId = classId;

    // 그룹 파라미터가 없으면(localStorage 기반) 현재 선택된 그룹 사용
    let resolvedGroup = memberGroup;
    if (typeof resolvedGroup === 'undefined') {
      try {
        if (typeof window !== 'undefined' && window.localStorage) {
          const key = `selected_member_group_${userId}_${classId}`;
          const saved = window.localStorage.getItem(key);
          if (saved) {
            resolvedGroup = saved;
          }
        }
      } catch {
        // ignore
      }
    }
    this.memberGroup = (resolvedGroup || 'A').toString().trim().toUpperCase();
  }

  private getAssignmentStatusesCollectionId(): string {
    return this.memberGroup === 'A'
      ? 'generalAssignmentStatuses'
      : `generalAssignmentStatuses_${this.memberGroup}`;
  }

  /**
   * 현재 문서 번호 조회
   */
  private async getCurrentDocumentNumber(): Promise<number> {
    const consolidatedRef = collection(
      db, 
      'users', 
      this.userId, 
      'classes', 
      this.classId, 
      this.getAssignmentStatusesCollectionId()
    );
    
    const q = query(consolidatedRef, orderBy('documentNumber', 'desc'), limit(1));
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return 0;
    }
    
    const lastDoc = snapshot.docs[0].data();
    return lastDoc.documentNumber || 0;
  }

  /**
   * 새로운 통합 문서 생성 (V2: 중첩 객체 형식)
   */
  private async createNewConsolidatedDocument(
    documentNumber: number,
    data: Partial<GeneralAssignmentConsolidated> | { assignments: { [key: string]: GeneralAssignmentInfoV2 } }
  ): Promise<string> {
    const docId = `generalAssignment-${documentNumber}`;
    const docRef = doc(
      db, 
      'users', 
      this.userId, 
      'classes', 
      this.classId, 
      this.getAssignmentStatusesCollectionId(), 
      docId
    );
    
    // V2: 중첩 객체로 저장
    const consolidatedDoc = {
      id: docId,
      classId: this.classId,
      lastUpdated: new Date(),
      documentNumber,
      assignments: this.useNestedObjects ? {} : [],
      createdAt: new Date(),
      totalSize: 0,
      ...data
    };
    
    await setDoc(docRef, {
      ...consolidatedDoc,
      lastUpdated: serverTimestamp(),
      createdAt: serverTimestamp()
    });
    this.invalidateConsolidatedCache();
    return docId;
  }

  /**
   * 문서 크기 계산
   */
  private calculateDocumentSize(doc: GeneralAssignmentConsolidated): number {
    return JSON.stringify(doc).length;
  }

  /**
   * 모든 통합 문서 조회 (캐시 사용; 쓰기 시 무효화)
   */
  public async getAllConsolidatedDocuments(): Promise<GeneralAssignmentConsolidated[]> {
    const key = this.getCacheKey();
    const cached = GeneralAssignmentConsolidatedService.__consolidatedCache.get(key);
    if (cached) return cached.v1;

    const consolidatedRef = collection(
      db, 
      'users', 
      this.userId, 
      'classes', 
      this.classId, 
      this.getAssignmentStatusesCollectionId()
    );
    
    const q = query(consolidatedRef, orderBy('documentNumber', 'asc'));
    const snapshot = await getDocs(q);
    
    const v1 = snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      let assignmentsArray: GeneralAssignmentInfo[] = [];
      if (data.assignments) {
        if (isArrayData(data.assignments)) {
          assignmentsArray = data.assignments;
        } else {
          assignmentsArray = assignmentsObjectToArray(data.assignments);
        }
      }
      return {
        ...data,
        id: docSnap.id,
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        assignments: assignmentsArray
      };
    }) as GeneralAssignmentConsolidated[];

    const v2 = snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      let assignmentsObj: { [assignmentId: string]: GeneralAssignmentInfoV2 } = {};
      if (data.assignments) {
        if (isArrayData(data.assignments)) {
          assignmentsObj = assignmentsArrayToObject(data.assignments);
        } else {
          assignmentsObj = data.assignments;
        }
      }
      return {
        ...data,
        id: docSnap.id,
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        assignments: assignmentsObj
      };
    }) as GeneralAssignmentConsolidatedV2[];

    GeneralAssignmentConsolidatedService.__consolidatedCache.set(key, { v1, v2 });
    return v1;
  }

  /**
   * 모든 통합 문서 조회 V2 (캐시 사용; 쓰기 시 무효화)
   */
  public async getAllConsolidatedDocumentsV2(): Promise<GeneralAssignmentConsolidatedV2[]> {
    const key = this.getCacheKey();
    const cached = GeneralAssignmentConsolidatedService.__consolidatedCache.get(key);
    if (cached) return cached.v2;

    const consolidatedRef = collection(
      db, 
      'users', 
      this.userId, 
      'classes', 
      this.classId, 
      this.getAssignmentStatusesCollectionId()
    );
    
    const q = query(consolidatedRef, orderBy('documentNumber', 'asc'));
    const snapshot = await getDocs(q);
    
    const v1 = snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      let assignmentsArray: GeneralAssignmentInfo[] = [];
      if (data.assignments) {
        if (isArrayData(data.assignments)) {
          assignmentsArray = data.assignments;
        } else {
          assignmentsArray = assignmentsObjectToArray(data.assignments);
        }
      }
      return {
        ...data,
        id: docSnap.id,
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        assignments: assignmentsArray
      };
    }) as GeneralAssignmentConsolidated[];

    const v2 = snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      let assignmentsObj: { [assignmentId: string]: GeneralAssignmentInfoV2 } = {};
      if (data.assignments) {
        if (isArrayData(data.assignments)) {
          assignmentsObj = assignmentsArrayToObject(data.assignments);
        } else {
          assignmentsObj = data.assignments;
        }
      }
      return {
        ...data,
        id: docSnap.id,
        lastUpdated: data.lastUpdated?.toDate() || new Date(),
        createdAt: data.createdAt?.toDate() || new Date(),
        assignments: assignmentsObj
      };
    }) as GeneralAssignmentConsolidatedV2[];

    GeneralAssignmentConsolidatedService.__consolidatedCache.set(key, { v1, v2 });
    return v2;
  }

  /**
   * 일반 과제 생성 (V2: 중첩 객체 형식)
   */
  async createGeneralAssignment(
    assignment: Assignment,
    title: string,
    description: string,
    dueDate: Date,
    basePoints: number,
    assignedStudentIds: string[]
  ): Promise<string> {
    const assignmentId = assignment.id;
    
    // V2: 초기 학생 상태를 중첩 객체로 생성
    const initialStudentStatuses: { [studentId: string]: GeneralStudentStatusV2 } = {};
    for (const studentId of assignedStudentIds) {
      initialStudentStatuses[studentId] = {
      status: 'pending' as AssignmentStatusValue,
      points: 0,
      updatedAt: new Date()
      };
    }

    // V2: 새로운 과제 정보 (중첩 객체)
    const assignmentInfoV2: GeneralAssignmentInfoV2 = {
      title,
      description: description || '',
      dueDate: dueDate.toISOString().split('T')[0],
      basePoints,
      assignedStudentIds,
      createdAt: new Date(),
      lastUpdated: new Date(),
      studentStatuses: initialStudentStatuses,
      categoryId: assignment.categoryId || '',
      isActive: assignment.isActive !== undefined ? assignment.isActive : true,
      icon: assignment.icon || '',
      notes: assignment.notes || '',
      updatedAt: new Date(),
      ...(assignment.rewardEnabled && {
        rewardEnabled: assignment.rewardEnabled,
        ...(assignment.rewardCode && { rewardCode: assignment.rewardCode }),
        ...(assignment.rewardMessage && { rewardMessage: assignment.rewardMessage }),
        ...(assignment.rewardCoins !== undefined && assignment.rewardCoins > 0 && { rewardCoins: assignment.rewardCoins }),
        ...(assignment.rewardItemCardIds && assignment.rewardItemCardIds.length > 0 && { rewardItemCardIds: assignment.rewardItemCardIds }),
        rewardGrantedStudentIds: assignment.rewardGrantedStudentIds || []
      })
    };

    // 기존 통합 문서들 조회 (V2 형식으로)
    const consolidatedDocsV2 = await this.getAllConsolidatedDocumentsV2();
    
    if (consolidatedDocsV2.length === 0) {
      // 첫 번째 문서 생성 (V2 중첩 객체 형식)
      const documentNumber = await this.getCurrentDocumentNumber() + 1;
      const docId = await this.createNewConsolidatedDocument(documentNumber, {
        assignments: { [assignmentId]: assignmentInfoV2 }
      });
      
      console.log('✅ 첫 번째 일반 과제 통합 문서 생성 완료 (V2):', {
        assignmentId,
        docId,
        assignmentCount: 1,
        studentCount: assignedStudentIds.length
      });
      
      return docId;
    }

    // 마지막 문서에 과제 추가
    const lastDocV2 = consolidatedDocsV2[consolidatedDocsV2.length - 1];
    lastDocV2.assignments[assignmentId] = assignmentInfoV2;
    
    const updatedSize = JSON.stringify(lastDocV2).length;

    if (updatedSize > this.maxDocumentSize) {
      // 문서 분할 필요 - 새 문서 생성
      const documentNumber = await this.getCurrentDocumentNumber() + 1;
      const docId = await this.createNewConsolidatedDocument(documentNumber, {
        assignments: { [assignmentId]: assignmentInfoV2 }
      });
      return docId;
    }

    // V2: 부분 업데이트 (해당 과제만 추가)
    const docRef = doc(
      db, 
      'users', 
      this.userId, 
      'classes', 
      this.classId, 
      this.getAssignmentStatusesCollectionId(), 
      lastDocV2.id
    );
    
    await updateDoc(docRef, {
      [`assignments.${assignmentId}`]: assignmentInfoV2,
      totalSize: updatedSize,
      lastUpdated: serverTimestamp()
    });
    this.invalidateConsolidatedCache();

    console.log('✅ 일반 과제 통합 문서에 과제 추가 완료 (V2):', {
        assignmentId,
      documentId: lastDocV2.id,
      assignmentCount: Object.keys(lastDocV2.assignments).length + 1,
        studentCount: assignedStudentIds.length
      });

    return lastDocV2.id;
  }

  /**
   * 멤버 상태 업데이트 (V2: 부분 업데이트 - dot notation)
   */
  async updateStudentStatus(
    assignmentId: string,
    studentId: string,
    status: AssignmentStatusValue,
    points: number,
    memo: string = '',
    submissionLink?: string,
    submissionMessage?: string,
    submittedAt?: Date,
    submissionImagePath?: string,
    submissionImageUpdatedAt?: string,
    submissionImageSizeBytes?: number,
    clearSubmissionImage?: boolean
  ): Promise<void> {
    try {
      // V2: 중첩 객체 형식으로 문서 조회
      const consolidatedDocsV2 = await this.getAllConsolidatedDocumentsV2();
      
      let targetDocV2: GeneralAssignmentConsolidatedV2 | null = null;
      let targetAssignmentV2: GeneralAssignmentInfoV2 | null = null;
      
      for (const docV2 of consolidatedDocsV2) {
        if (docV2.assignments[assignmentId]) {
          targetDocV2 = docV2;
          targetAssignmentV2 = docV2.assignments[assignmentId];
          break;
        }
      }

      if (!targetDocV2 || !targetAssignmentV2) {
        throw new Error(`과제 ${assignmentId}를 찾을 수 없습니다.`);
      }

      // 이전 상태 확인 (보상 처리용)
      const existingStatus = targetAssignmentV2.studentStatuses?.[studentId];
      const oldStatus = existingStatus?.status || 'pending';
      
      // 완료 상태 여부 확인
      const isCompletedStatus = (s: AssignmentStatusValue) => 
        ['confirmed', 'delayed', 'exempted'].includes(s);
      const wasCompleted = isCompletedStatus(oldStatus);
      const isNowCompleted = isCompletedStatus(status);

      // V2: 새 학생 상태 (중첩 객체)
      const studentStatusV2: GeneralStudentStatusV2 = {
        status,
        points,
        updatedAt: new Date(),
        ...(submissionLink && { submissionLink }),
        ...(submissionMessage && { submissionMessage }),
        ...(submittedAt && { submittedAt })
      };

      if (!clearSubmissionImage) {
        if (submissionImagePath !== undefined) {
          if (submissionImagePath) studentStatusV2.submissionImagePath = submissionImagePath;
        } else if (existingStatus?.submissionImagePath) {
          studentStatusV2.submissionImagePath = existingStatus.submissionImagePath;
        }
        if (submissionImageUpdatedAt !== undefined) {
          if (submissionImageUpdatedAt) studentStatusV2.submissionImageUpdatedAt = submissionImageUpdatedAt;
        } else if (existingStatus?.submissionImageUpdatedAt) {
          studentStatusV2.submissionImageUpdatedAt = existingStatus.submissionImageUpdatedAt;
        }
        if (submissionImageSizeBytes !== undefined) {
          studentStatusV2.submissionImageSizeBytes = submissionImageSizeBytes;
        } else if (existingStatus?.submissionImageSizeBytes !== undefined) {
          studentStatusV2.submissionImageSizeBytes = existingStatus.submissionImageSizeBytes;
        }
      }

      // V2: 부분 업데이트 (해당 학생만) - dot notation 사용
      const docRef = doc(
        db, 
        'users', 
        this.userId, 
        'classes', 
        this.classId, 
        this.getAssignmentStatusesCollectionId(), 
        targetDocV2.id
      );
      
      await updateDoc(docRef, {
        [`assignments.${assignmentId}.studentStatuses.${studentId}`]: studentStatusV2,
        [`assignments.${assignmentId}.lastUpdated`]: new Date(),
        lastUpdated: serverTimestamp()
      });
      this.invalidateConsolidatedCache();

      // 보상 처리 (상태 변경 시)
      if (wasCompleted !== isNowCompleted) {
        // 배열 형식으로 변환하여 보상 처리 (기존 로직 호환)
        const targetAssignment: GeneralAssignmentInfo = {
          assignmentId,
          ...targetAssignmentV2,
          studentStatuses: studentStatusesObjectToArray(targetAssignmentV2.studentStatuses || {})
        };
        
        await this.processAssignmentRewards(
          assignmentId,
          studentId,
          targetAssignment,
          wasCompleted,
          isNowCompleted
        );
      }

      console.log('✅ 일반 과제 멤버 상태 업데이트 완료 (V2 부분 업데이트):', {
        assignmentId,
        studentId,
        status,
        points
      });

    } catch (error) {
      console.error('❌ 일반 과제 멤버 상태 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 과제 완료 보상 처리 (자동)
   */
  private async processAssignmentRewards(
    assignmentId: string,
    studentId: string,
    assignment: GeneralAssignmentInfo,
    wasCompleted: boolean,
    isNowCompleted: boolean
  ): Promise<void> {
    try {
      // 보상이 활성화되어 있지 않으면 처리하지 않음
      if (!assignment.rewardEnabled) {
        return;
      }

      const hasCoins = assignment.rewardCoins && assignment.rewardCoins > 0;
      const hasItemCards = assignment.rewardItemCardIds && assignment.rewardItemCardIds.length > 0;

      if (!hasCoins && !hasItemCards) {
        return;
      }

      // 동적 import로 순환 참조 방지
      const { ItemCardService } = await import('./itemCardService');
      const { StudentCoinService } = await import('./studentCoinService');
      
      const itemCardService = new ItemCardService(this.userId, this.classId, this.memberGroup);
      const coinService = new StudentCoinService(this.userId, this.classId, this.memberGroup);

      if (isNowCompleted && !wasCompleted) {
        // 완료 상태로 변경: 보상 지급
        console.log(`🎁 과제 완료 보상 지급 시작: ${assignmentId} - ${studentId}`);
        console.log(`🔍 과제 보상 정보:`, {
          rewardEnabled: assignment.rewardEnabled,
          rewardCoins: assignment.rewardCoins,
          rewardItemCardIds: assignment.rewardItemCardIds,
          rewardGrantedStudentIds: assignment.rewardGrantedStudentIds
        });

        // 이미 보상을 받았는지 확인
        const alreadyGranted = assignment.rewardGrantedStudentIds?.includes(studentId);
        console.log(`🔍 보상 지급 여부 확인:`, { studentId, alreadyGranted, rewardGrantedStudentIds: assignment.rewardGrantedStudentIds });

        if (alreadyGranted) {
          // 이미 보상을 받았지만 잠금 상태일 수 있으므로 잠금 해제만 처리
          console.log(`📝 이미 보상을 받은 학생 - 잠금 해제만 처리: ${studentId}`);
          if (hasItemCards) {
            await itemCardService.unlockStudentItemCardsByAssignment(studentId, assignmentId);
          }

          // 캐시 무효화 - 보상 상태 변경 즉시 반영
          try {
            const { userDataService } = await import('../services/userDataService');
            userDataService.invalidateMembersCache(this.userId, this.classId, this.memberGroup);
            console.log('🗑️ 과제 보상 잠금 해제 후 캐시 무효화 완료');
          } catch (error) {
            console.warn('과제 보상 잠금 해제 후 캐시 무효화 실패 (무시):', error);
          }

          return;
        }

        // 코인 지급
        if (hasCoins) {
          await coinService.grantCoinsToStudent(
            studentId,
            assignment.rewardCoins!,
            `${assignment.title} 과제 완료 보상`,
            'General'
          );
          console.log(`💰 코인 지급: ${assignment.rewardCoins}코인`);
        }

        // 아이템 카드 지급
        if (hasItemCards) {
          // 사용자 플랜 확인
          const { userDataService } = await import('../services/userDataService');
          const userProfile = await userDataService.loadUserProfile(this.userId);

          if (userProfile?.userType === 'free' || userProfile?.userType === 'basic') {
            console.log('⚠️ 프리/베이직 플랜: 아이템 카드 보상 스킵');
            console.log('📢 표시할 메시지: 스탠다드 이상의 플랜에서 아이템카드 사용이 가능합니다. 과제에 설정된 아이템 카드는 지급되지 않습니다. 아이템 카드를 사용하기 위해서는 플랜을 업그레이드 해주세요.');

            // 아이템 카드 지급 스킵
            // 메시지를 표시하기 위한 플래그를 설정하거나, 별도의 알림 시스템을 사용할 수 있음
          } else {
            const itemCardSet = await itemCardService.loadItemCardSet();
            console.log('🎁 과제 보상 아이템 카드 세트 로드:', itemCardSet ? '성공' : '실패');
            if (itemCardSet) {
              console.log('🎁 assignmentItem 개수:', itemCardSet.assignmentItem?.length || 0);
              console.log('🎁 보상으로 설정된 itemCardIds:', assignment.rewardItemCardIds);

              const rewardItemCards = (assignment.rewardItemCardIds || [])
                .map(itemCardId => {
                  const foundItem = itemCardSet.assignmentItem.find(item => item.itemCardId === itemCardId);
                  console.log(`🎁 아이템 검색 결과 - ${itemCardId}:`, foundItem ? '찾음' : '찾을 수 없음');
                  return foundItem;
                })
                .filter((item): item is any => item !== undefined);

              const studentItemCards = await itemCardService.getStudentItemCards(studentId);
              const existingRewards = studentItemCards.filter(card => card.assignmentId === assignmentId);

              if (existingRewards.length > 0) {
                await itemCardService.unlockStudentItemCardsByAssignment(studentId, assignmentId);
              }

              const newRewards = rewardItemCards.filter(rewardItem =>
                !existingRewards.some(existing => existing.itemCardId === rewardItem.itemCardId)
              );

              for (const rewardItem of newRewards) {
                const studentItemCard: any = {
                  instanceId: `assignment_${assignmentId}_${rewardItem.itemCardId}_${Date.now()}`,
                  itemCardId: rewardItem.itemCardId,
                  type: rewardItem.type,
                  ...(rewardItem.useCount !== undefined && { remainingUseCount: rewardItem.useCount }),
                  acquiredAt: new Date(),
                  assignmentId: assignmentId,
                  category: 'assignment',
                  isLocked: false
                };

                await itemCardService.addStudentItemCardAtomic(studentId, studentItemCard);
                console.log(`🎁 아이템 보상 지급: ${rewardItem.name}`);
              }
            }
          }
        }

        // rewardGrantedStudentIds 업데이트
        const updatedGrantedIds = [...(assignment.rewardGrantedStudentIds || []), studentId];
        await this.updateGeneralAssignment(
          assignmentId,
          assignment.title,
          assignment.description || '',
          new Date(assignment.dueDate),
          assignment.basePoints,
          assignment.assignedStudentIds,
          assignment.categoryId,
          assignment.isActive,
          assignment.icon,
          assignment.notes,
          assignment.rewardEnabled,
          assignment.rewardCode,
          assignment.rewardMessage,
          assignment.rewardCoins,
          assignment.rewardItemCardIds,
          updatedGrantedIds
        );

      } else if (!isNowCompleted && wasCompleted) {
        // 미완료 상태로 변경: 보상 회수 (이미 '선택 멤버 보상 회수'로 회수된 경우 생략)
        const stillGranted = assignment.rewardGrantedStudentIds?.includes(studentId);
        if (!stillGranted) {
          console.log(`⏳ 과제 미완료로 변경하지만 보상은 이미 회수됨 - 코인/아이템 보정 생략: ${assignmentId} - ${studentId}`);
          return;
        }
        console.log(`⏳ 과제 미완료 보상 회수 시작: ${assignmentId} - ${studentId}`);

        // 코인 회수 (음수로 지급)
        if (hasCoins) {
          await coinService.grantCoinsToStudent(
            studentId,
            -assignment.rewardCoins!,
            `${assignment.title} 과제 보상 회수`,
            'General'
          );
          console.log(`💰 코인 회수: -${assignment.rewardCoins}코인`);
        }

        // 아이템 카드 잠금 (제거하지 않고 잠금 처리)
        if (hasItemCards) {
          await itemCardService.lockStudentItemCardsByAssignment(studentId, assignmentId);
          console.log(`🔒 아이템 카드 잠금 완료`);
        }

        // 캐시 무효화 - 보상 회수 즉시 반영
        try {
          const { userDataService } = await import('../services/userDataService');
          userDataService.invalidateMembersCache(this.userId, this.classId, this.memberGroup);
          console.log('🗑️ 과제 보상 회수 후 캐시 무효화 완료');
        } catch (error) {
          console.warn('과제 보상 회수 후 캐시 무효화 실패 (무시):', error);
        }

        // rewardGrantedStudentIds에서 제거 (보상을 회수했으므로 다시 완료하면 보상을 재지급할 수 있도록)
        const updatedGrantedIds = (assignment.rewardGrantedStudentIds || []).filter(id => id !== studentId);
        await this.updateGeneralAssignment(
          assignmentId,
          assignment.title,
          assignment.description || '',
          new Date(assignment.dueDate),
          assignment.basePoints,
          assignment.assignedStudentIds,
          assignment.categoryId,
          assignment.isActive,
          assignment.icon,
          assignment.notes,
          assignment.rewardEnabled,
          assignment.rewardCode,
          assignment.rewardMessage,
          assignment.rewardCoins,
          assignment.rewardItemCardIds,
          updatedGrantedIds
        );
        console.log('✅ 보상 회수 완료 및 기록 업데이트');
      }

    } catch (error) {
      console.error('❌ 과제 보상 처리 실패:', error);
      // 보상 처리 실패해도 상태 업데이트는 완료되도록 에러를 throw하지 않음
    }

    // 데이터 변경 후 캐시 무효화 (다음 조회 시 최신 데이터 반영)
    try {
      const { userDataService } = await import('../services/userDataService');
      userDataService.invalidateMembersCache(this.userId, this.classId, this.memberGroup);
      console.log('🗑️ 데이터 변경 후 멤버 캐시 무효화 완료');
    } catch (error) {
      console.warn('캐시 무효화 실패 (무시):', error);
    }
  }

  /**
   * 여러 멤버의 상태를 배치로 업데이트 (V2: 부분 업데이트 - dot notation)
   */
  async batchUpdateStudentStatuses(
    assignmentId: string,
    studentUpdates: Array<{
      studentId: string;
      status: AssignmentStatusValue;
      points: number;
      memo?: string;
      submissionLink?: string;
      submissionMessage?: string;
      submittedAt?: Date;
      submissionImagePath?: string;
      submissionImageUpdatedAt?: string;
      submissionImageSizeBytes?: number;
    }>
  ): Promise<void> {
    try {
      // V2: 중첩 객체 형식으로 문서 조회
      const consolidatedDocsV2 = await this.getAllConsolidatedDocumentsV2();
      
      let targetDocV2: GeneralAssignmentConsolidatedV2 | null = null;
      let targetAssignmentV2: GeneralAssignmentInfoV2 | null = null;
      
      for (const docV2 of consolidatedDocsV2) {
        if (docV2.assignments[assignmentId]) {
          targetDocV2 = docV2;
          targetAssignmentV2 = docV2.assignments[assignmentId];
          break;
        }
      }

      if (!targetDocV2 || !targetAssignmentV2) {
        throw new Error(`과제 ${assignmentId}를 찾을 수 없습니다.`);
      }

      // 보상 처리를 위해 이전 상태 저장
      const oldStatusMap = new Map<string, AssignmentStatusValue>();
      for (const update of studentUpdates) {
        const existingStatus = targetAssignmentV2.studentStatuses?.[update.studentId];
        oldStatusMap.set(update.studentId, existingStatus?.status || 'pending');
      }

      // V2: 부분 업데이트 객체 생성 (dot notation)
      const updateObj: { [key: string]: any } = {
        [`assignments.${assignmentId}.lastUpdated`]: new Date(),
        lastUpdated: serverTimestamp()
      };

      for (const update of studentUpdates) {
        const existingStatus = targetAssignmentV2.studentStatuses?.[update.studentId];
        
        const studentStatusV2: GeneralStudentStatusV2 = {
          status: update.status,
          points: update.points,
          updatedAt: new Date()
        };
        
        // 제출 확인 데이터 처리
        if (Object.prototype.hasOwnProperty.call(update, 'submissionLink')) {
          if (update.submissionLink !== undefined) {
            studentStatusV2.submissionLink = update.submissionLink;
          }
        } else if (existingStatus?.submissionLink) {
          studentStatusV2.submissionLink = existingStatus.submissionLink;
        }
        
        if (Object.prototype.hasOwnProperty.call(update, 'submissionMessage')) {
          if (update.submissionMessage !== undefined) {
            studentStatusV2.submissionMessage = update.submissionMessage;
          }
        } else if (existingStatus?.submissionMessage) {
          studentStatusV2.submissionMessage = existingStatus.submissionMessage;
        }
        
        if (Object.prototype.hasOwnProperty.call(update, 'submittedAt')) {
          if (update.submittedAt !== undefined) {
            studentStatusV2.submittedAt = update.submittedAt;
          }
        } else if (existingStatus?.submittedAt) {
          studentStatusV2.submittedAt = existingStatus.submittedAt;
        }

        if (Object.prototype.hasOwnProperty.call(update, 'submissionImagePath')) {
          if (update.submissionImagePath !== undefined) {
            studentStatusV2.submissionImagePath = update.submissionImagePath;
          }
        } else if (existingStatus?.submissionImagePath) {
          studentStatusV2.submissionImagePath = existingStatus.submissionImagePath;
        }
        if (Object.prototype.hasOwnProperty.call(update, 'submissionImageUpdatedAt')) {
          if (update.submissionImageUpdatedAt !== undefined) {
            studentStatusV2.submissionImageUpdatedAt = update.submissionImageUpdatedAt;
          }
        } else if (existingStatus?.submissionImageUpdatedAt) {
          studentStatusV2.submissionImageUpdatedAt = existingStatus.submissionImageUpdatedAt;
        }
        if (Object.prototype.hasOwnProperty.call(update, 'submissionImageSizeBytes')) {
          if (update.submissionImageSizeBytes !== undefined) {
            studentStatusV2.submissionImageSizeBytes = update.submissionImageSizeBytes;
          }
        } else if (existingStatus?.submissionImageSizeBytes !== undefined) {
          studentStatusV2.submissionImageSizeBytes = existingStatus.submissionImageSizeBytes;
        }

        // V2: 각 학생별 부분 업데이트 경로 추가
        updateObj[`assignments.${assignmentId}.studentStatuses.${update.studentId}`] = studentStatusV2;
      }

      // V2: 한 번의 updateDoc으로 모든 학생 상태 업데이트
      const docRef = doc(
        db, 
        'users', 
        this.userId, 
        'classes', 
        this.classId, 
        this.getAssignmentStatusesCollectionId(), 
        targetDocV2.id
      );
      
      await updateDoc(docRef, updateObj);
      this.invalidateConsolidatedCache();

      console.log('✅ 일반 과제 멤버 상태 배치 업데이트 완료 (V2 부분 업데이트):', {
        assignmentId,
        studentCount: studentUpdates.length
      });

      // =========================
      // 보상 처리 (상태 변경 시)
      // =========================
      const isCompletedStatus = (s: AssignmentStatusValue) => 
        ['confirmed', 'delayed', 'exempted'].includes(s);
      
      for (const update of studentUpdates) {
        const oldStatus = oldStatusMap.get(update.studentId) || 'pending';
        const wasCompleted = isCompletedStatus(oldStatus);
        const isNowCompleted = isCompletedStatus(update.status);

        if (wasCompleted !== isNowCompleted) {
          // 최신 과제 정보 읽기 (배열 형식으로 변환)
          const freshConsolidatedDocs = await this.getAllConsolidatedDocuments();
          let freshTargetAssignment: GeneralAssignmentInfo | null = null;

          for (const freshDoc of freshConsolidatedDocs) {
            const foundAssignment = freshDoc.assignments.find(a => a.assignmentId === assignmentId);
            if (foundAssignment) {
              freshTargetAssignment = foundAssignment;
              break;
            }
          }

          if (!freshTargetAssignment) {
            console.error(`❌ 보상 처리 중 과제 정보를 찾을 수 없음: ${assignmentId}`);
            continue;
          }

          await this.processAssignmentRewards(
            assignmentId,
            update.studentId,
            freshTargetAssignment,
            wasCompleted,
            isNowCompleted
          );
        }
      }

    } catch (error) {
      console.error('❌ 일반 과제 멤버 상태 배치 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 일반 과제 업데이트 (V2: 부분 업데이트 - dot notation)
   */
  async updateGeneralAssignment(
    assignmentId: string,
    title: string,
    description: string,
    dueDate: Date,
    basePoints: number,
    assignedStudentIds: string[],
    categoryId?: string,
    isActive?: boolean,
    icon?: string,
    notes?: string,
    rewardEnabled?: boolean,
    rewardCode?: string,
    rewardMessage?: string,
    rewardCoins?: number,
    rewardItemCardIds?: string[],
    rewardGrantedStudentIds?: string[]
  ): Promise<void> {
    try {
      // V2: 중첩 객체 형식으로 문서 조회
      const consolidatedDocsV2 = await this.getAllConsolidatedDocumentsV2();
      
      let targetDocV2: GeneralAssignmentConsolidatedV2 | null = null;
      let targetAssignmentV2: GeneralAssignmentInfoV2 | null = null;
      
      for (const docV2 of consolidatedDocsV2) {
        if (docV2.assignments[assignmentId]) {
          targetDocV2 = docV2;
          targetAssignmentV2 = docV2.assignments[assignmentId];
          break;
        }
      }

      if (!targetDocV2 || !targetAssignmentV2) {
        throw new Error(`과제 ${assignmentId}를 찾을 수 없습니다.`);
      }

      // V2: 부분 업데이트 객체 생성
      const updateObj: { [key: string]: any } = {
        [`assignments.${assignmentId}.title`]: title,
        [`assignments.${assignmentId}.description`]: description || '',
        [`assignments.${assignmentId}.dueDate`]: dueDate.toISOString().split('T')[0],
        [`assignments.${assignmentId}.basePoints`]: basePoints,
        [`assignments.${assignmentId}.assignedStudentIds`]: assignedStudentIds,
        [`assignments.${assignmentId}.lastUpdated`]: new Date(),
        [`assignments.${assignmentId}.updatedAt`]: new Date(),
        lastUpdated: serverTimestamp()
      };

      // 추가 필드들 업데이트
      if (categoryId !== undefined) updateObj[`assignments.${assignmentId}.categoryId`] = categoryId;
      if (isActive !== undefined) updateObj[`assignments.${assignmentId}.isActive`] = isActive;
      if (icon !== undefined) updateObj[`assignments.${assignmentId}.icon`] = icon;
      if (notes !== undefined) updateObj[`assignments.${assignmentId}.notes`] = notes;

      // 보상 필드 처리
      if (rewardEnabled !== undefined) {
        updateObj[`assignments.${assignmentId}.rewardEnabled`] = rewardEnabled;
      }
      
      if (rewardEnabled === false) {
        // rewardEnabled가 false이면 보상 필드 삭제
        updateObj[`assignments.${assignmentId}.rewardCode`] = deleteField();
        updateObj[`assignments.${assignmentId}.rewardMessage`] = deleteField();
        updateObj[`assignments.${assignmentId}.rewardCoins`] = deleteField();
        updateObj[`assignments.${assignmentId}.rewardItemCardIds`] = deleteField();
        updateObj[`assignments.${assignmentId}.rewardGrantedStudentIds`] = deleteField();
      } else if (rewardEnabled === true) {
        if (rewardCode) updateObj[`assignments.${assignmentId}.rewardCode`] = rewardCode;
        else if (rewardCode === '') updateObj[`assignments.${assignmentId}.rewardCode`] = deleteField();
        
        if (rewardMessage) updateObj[`assignments.${assignmentId}.rewardMessage`] = rewardMessage;
        else if (rewardMessage === '') updateObj[`assignments.${assignmentId}.rewardMessage`] = deleteField();
        
        if (rewardCoins !== undefined && rewardCoins > 0) updateObj[`assignments.${assignmentId}.rewardCoins`] = rewardCoins;
        else if (rewardCoins === 0) updateObj[`assignments.${assignmentId}.rewardCoins`] = deleteField();
        
        if (rewardItemCardIds && rewardItemCardIds.length > 0) updateObj[`assignments.${assignmentId}.rewardItemCardIds`] = rewardItemCardIds;
        else if (rewardItemCardIds && rewardItemCardIds.length === 0) updateObj[`assignments.${assignmentId}.rewardItemCardIds`] = deleteField();
      }
      
      if (rewardGrantedStudentIds !== undefined) {
        if (rewardGrantedStudentIds.length > 0) updateObj[`assignments.${assignmentId}.rewardGrantedStudentIds`] = rewardGrantedStudentIds;
        else updateObj[`assignments.${assignmentId}.rewardGrantedStudentIds`] = deleteField();
      }

      // 멤버 변경 처리
      const previousStudentIds = targetAssignmentV2.assignedStudentIds || [];
      const newStudentIds = assignedStudentIds || [];
      
      // 제거된 멤버들의 상태 삭제
      const removedStudentIds = previousStudentIds.filter(id => !newStudentIds.includes(id));
      for (const removedId of removedStudentIds) {
        updateObj[`assignments.${assignmentId}.studentStatuses.${removedId}`] = deleteField();
      }
      
      // 새로 추가된 멤버들의 초기 상태 생성
      const addedStudentIds = newStudentIds.filter(id => !previousStudentIds.includes(id));
      for (const addedId of addedStudentIds) {
        updateObj[`assignments.${assignmentId}.studentStatuses.${addedId}`] = {
        status: 'pending' as AssignmentStatusValue,
        points: 0,
        updatedAt: new Date()
        } as GeneralStudentStatusV2;
      }

      // V2: 한 번의 updateDoc으로 모든 필드 업데이트
      const docRef = doc(
        db, 
        'users', 
        this.userId, 
        'classes', 
        this.classId, 
        this.getAssignmentStatusesCollectionId(), 
        targetDocV2.id
      );
      
      await updateDoc(docRef, updateObj);
      this.invalidateConsolidatedCache();

      console.log('✅ 일반 과제 통합 문서 업데이트 완료 (V2 부분 업데이트):', {
        assignmentId,
        documentId: targetDocV2.id,
        studentCount: assignedStudentIds.length
      });

    } catch (error) {
      console.error('❌ 일반 과제 통합 문서 업데이트 실패:', error);
      throw error;
    }
  }

  /**
   * 일반 과제 삭제 (V2: deleteField() 사용 - 해당 과제만 삭제)
   */
  async deleteGeneralAssignment(assignmentId: string): Promise<void> {
    try {
      // V2: 중첩 객체 형식으로 문서 조회
      const consolidatedDocsV2 = await this.getAllConsolidatedDocumentsV2();
      
      let targetDocV2: GeneralAssignmentConsolidatedV2 | null = null;
      
      for (const docV2 of consolidatedDocsV2) {
        if (docV2.assignments[assignmentId]) {
          targetDocV2 = docV2;
          break;
        }
      }

      if (!targetDocV2) {
        throw new Error(`과제 ${assignmentId}를 찾을 수 없습니다.`);
      }

      // V2: deleteField()를 사용하여 해당 과제만 삭제
      const docRef = doc(
        db, 
        'users', 
        this.userId, 
        'classes', 
        this.classId, 
        this.getAssignmentStatusesCollectionId(), 
        targetDocV2.id
      );
      
      await updateDoc(docRef, {
        [`assignments.${assignmentId}`]: deleteField(),
        lastUpdated: serverTimestamp()
      });
      this.invalidateConsolidatedCache();

      console.log('✅ 일반 과제 통합 문서에서 과제 삭제 완료 (V2):', {
        assignmentId,
        documentId: targetDocV2.id,
        remainingAssignments: Object.keys(targetDocV2.assignments).length - 1
      });

      try {
        await assignmentSubmissionImageService.deleteGeneralAssignmentImages(
          this.userId,
          this.classId,
          this.memberGroup,
          assignmentId
        );
      } catch (storageError) {
        console.warn('과제 제출 이미지 Storage 삭제 실패 (무시):', storageError);
      }

    } catch (error) {
      console.error('❌ 일반 과제 통합 문서에서 과제 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 학생 상태 삭제 (V2: deleteField() 사용)
   */
  async deleteStudentStatus(assignmentId: string, studentId: string): Promise<void> {
    try {
      const consolidatedDocsV2 = await this.getAllConsolidatedDocumentsV2();
      
      let targetDocV2: GeneralAssignmentConsolidatedV2 | null = null;
      
      for (const docV2 of consolidatedDocsV2) {
        if (docV2.assignments[assignmentId]) {
          targetDocV2 = docV2;
          break;
        }
      }

      if (!targetDocV2) {
        throw new Error(`과제 ${assignmentId}를 찾을 수 없습니다.`);
      }

      const docRef = doc(
        db, 
        'users', 
        this.userId, 
        'classes', 
        this.classId, 
        this.getAssignmentStatusesCollectionId(), 
        targetDocV2.id
      );
      
      await updateDoc(docRef, {
        [`assignments.${assignmentId}.studentStatuses.${studentId}`]: deleteField(),
        lastUpdated: serverTimestamp()
      });
      this.invalidateConsolidatedCache();

      console.log('✅ 학생 상태 삭제 완료 (V2):', { assignmentId, studentId });

    } catch (error) {
      console.error('❌ 학생 상태 삭제 실패:', error);
      throw error;
    }
  }

  /**
   * 문서 분할
   */
  private async splitDocument(
    targetDoc: GeneralAssignmentConsolidated,
    targetDocIndex: number
  ): Promise<void> {
    try {
      console.log('🔄 일반 과제 문서 분할 시작:', {
        documentId: targetDoc.id,
        currentSize: targetDoc.totalSize,
        maxSize: this.maxDocumentSize
      });

      const midIndex = Math.ceil(targetDoc.assignments.length / 2);
      const firstHalf = targetDoc.assignments.slice(0, midIndex);
      const secondHalf = targetDoc.assignments.slice(midIndex);

      // 첫 번째 문서 업데이트
      const firstDoc: GeneralAssignmentConsolidated = {
        ...targetDoc,
        assignments: firstHalf,
        totalSize: this.calculateDocumentSize({ ...targetDoc, assignments: firstHalf })
      };

      const firstDocRef = doc(
        db, 
        'users', 
        this.userId, 
        'classes', 
        this.classId, 
        this.getAssignmentStatusesCollectionId(), 
        firstDoc.id
      );
      
      await updateDoc(firstDocRef, {
        assignments: firstDoc.assignments,
        totalSize: firstDoc.totalSize,
        lastUpdated: serverTimestamp()
      });
      this.invalidateConsolidatedCache();

      // 두 번째 문서 생성
      const nextDocumentNumber = await this.getCurrentDocumentNumber() + 1;
      const secondDocId = await this.createNewConsolidatedDocument(nextDocumentNumber, {
        assignments: secondHalf,
        totalSize: this.calculateDocumentSize({ ...targetDoc, assignments: secondHalf })
      });

      console.log('✅ 일반 과제 문서 분할 완료:', {
        firstDocument: firstDoc.id,
        secondDocument: secondDocId,
        firstHalfAssignments: firstHalf.length,
        secondHalfAssignments: secondHalf.length
      });

    } catch (error) {
      console.error('❌ 일반 과제 문서 분할 실패:', error);
      throw error;
    }
  }

  /**
   * 모든 일반 과제 조회
   */
  async loadAllGeneralAssignments(): Promise<GeneralAssignmentInfo[]> {
    try {
      const consolidatedDocs = await this.getAllConsolidatedDocuments();
      
      const allAssignments: GeneralAssignmentInfo[] = [];
      consolidatedDocs.forEach(doc => {
        // Timestamp를 Date로 변환하면서 과제 추가
        const assignments = doc.assignments.map(assignment => {
          // createdAt과 updatedAt을 Date로 변환
          const createdAt = assignment.createdAt instanceof Date ? assignment.createdAt : 
                           (assignment.createdAt as any)?.toDate?.() || 
                           (typeof assignment.createdAt === 'string' ? new Date(assignment.createdAt) : new Date());
          const updatedAt = assignment.updatedAt instanceof Date ? assignment.updatedAt : 
                           (assignment.updatedAt as any)?.toDate?.() || 
                           (typeof assignment.updatedAt === 'string' ? new Date(assignment.updatedAt) : new Date());
          const lastUpdated = assignment.lastUpdated instanceof Date ? assignment.lastUpdated : 
                             (assignment.lastUpdated as any)?.toDate?.() || 
                             (typeof assignment.lastUpdated === 'string' ? new Date(assignment.lastUpdated) : new Date());
          
          return {
          ...assignment,
            createdAt,
            updatedAt,
            lastUpdated,
          studentStatuses: assignment.studentStatuses.map(status => {
              const statusUpdatedAt = status.updatedAt instanceof Date ? status.updatedAt : 
                            (status.updatedAt as any)?.toDate?.() || new Date();
            const submittedAt = status.submittedAt ? 
                              (status.submittedAt instanceof Date ? status.submittedAt : 
                              (status.submittedAt as any)?.toDate?.() || new Date()) : 
                              undefined;
            
            return {
              ...status,
                updatedAt: statusUpdatedAt,
              submittedAt
            };
          })
          };
        });
        allAssignments.push(...assignments);
      });

      console.log('✅ 모든 일반 과제 조회 완료:', {
        documentCount: consolidatedDocs.length,
        assignmentCount: allAssignments.length
      });

      return allAssignments;

    } catch (error) {
      console.error('❌ 일반 과제 조회 실패:', error);
      throw error;
    }
  }

  /**
   * 특정 과제 조회
   */
  async loadGeneralAssignment(assignmentId: string): Promise<GeneralAssignmentInfo | null> {
    try {
      const allAssignments = await this.loadAllGeneralAssignments();
      const assignment = allAssignments.find(a => a.assignmentId === assignmentId) || null;
      
      // 제출 확인 데이터 디버깅
      if (assignment) {
        const submissionStatuses = assignment.studentStatuses.filter((s: any) => s.submissionLink || s.submissionMessage);
        if (submissionStatuses.length > 0) {
          console.log(`🔍 과제 ${assignmentId} 로드 - 제출 확인 데이터가 있는 멤버:`, {
            count: submissionStatuses.length,
            students: submissionStatuses.map((s: any) => ({
              studentId: s.studentId,
              submissionLink: s.submissionLink,
              submissionMessage: s.submissionMessage
            }))
          });
        }
      }
      
      return assignment;
    } catch (error) {
      console.error('❌ 특정 일반 과제 조회 실패:', error);
      throw error;
    }
  }
}
